import * as React from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Checkbox from "@mui/material/Checkbox";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import cornerstoneTools from "cornerstone-tools";
import {
  FLUID_MANUAL_BRUSH_RADIUS_MIN,
  FLUID_MANUAL_BRUSH_RADIUS_MAX,
  FLUID_SEGMENTATION_LABELS,
  fluidLabelTextColor,
  clampFluidManualBrushRadius,
} from "../utils/fluidManualBrush";

export default function FluidManualTargetSelector() {
  const dispatch = useDispatch();
  const dicom_data = useSelector((state) => state.dcm);
  const manualFluidTargetLabel = useSelector(
    (state) => state.dcm.manualFluidTargetLabel,
  );
  const activeFluidLabels = FLUID_SEGMENTATION_LABELS;

  const selectLabel = (label) => {
    dispatch({ type: "SET_MANUAL_FLUID_TARGET_LABEL", label });
    cornerstoneTools.manualFluidTargetLabel = label;
  };

  const toggleVisibility = (label) => {
    const currentVisible =
      dicom_data?.local_settings?.FluidSegmentation?.[label] ?? true;
    const nextVisible = !currentVisible;

    dispatch({
      type: "SET_FLUID_SEGMENTATION_VISIBILITY_LOCAL",
      label,
      value: nextVisible,
    });

    if (!nextVisible && manualFluidTargetLabel === label) {
      dispatch({ type: "SET_MANUAL_FLUID_TARGET_LABEL", label: null });
      cornerstoneTools.manualFluidTargetLabel = null;
    }
  };

  React.useEffect(() => {
    cornerstoneTools.manualFluidTargetLabel = manualFluidTargetLabel || null;
  }, [manualFluidTargetLabel]);

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.5,
        bgcolor: "#e8eaed",
        borderBottom: "1px solid #cdd1d6",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, color: "text.primary", fontWeight: 600 }}
      >
        Fluid for manual brush & erase
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: "block", mb: 1, color: "text.secondary" }}
      >
        Select one label to enable Brush, Paint, and Segmentation Eraser tools.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {activeFluidLabels.map((label) => {
          const visible =
            dicom_data?.local_settings?.FluidSegmentation?.[label] ?? true;
          const isSelected = visible && manualFluidTargetLabel === label;

          return (
            <Box
              key={label}
              sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Checkbox
                size="small"
                checked={visible}
                onChange={() => toggleVisibility(label)}
                sx={{ p: 0.25 }}
              />
              <Box
                role="button"
                tabIndex={visible ? 0 : -1}
                aria-disabled={!visible}
                onClick={() => visible && selectLabel(label)}
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "8px",
                  border: isSelected
                    ? "2px solid #1976d2"
                    : "1px solid #b8bcc4",
                  bgcolor: isSelected ? "#e3f2fd" : "#f4f5f7",
                  px: 1.5,
                  py: 1,
                  cursor: visible ? "pointer" : "not-allowed",
                  opacity: visible ? 1 : 0.45,
                }}
              >
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: 600, color: fluidLabelTextColor(label) }}
                >
                  {label} [S]
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export function FluidBrushStrokeFillOptions() {
  const dispatch = useDispatch();
  const selectedTool = useSelector((state) => state.dcm.selectedTool);
  const manualFluidTargetLabel = useSelector(
    (state) => state.dcm.manualFluidTargetLabel,
  );
  const mode = useSelector((state) => state.dcm.fluidBrushDrawingMode);
  const fluidManualBrushRadius = useSelector(
    (state) => state.dcm.fluidManualBrushRadius,
  );

  const paintToolsActive = selectedTool === "Paint";
  if (!paintToolsActive || !manualFluidTargetLabel) {
    return null;
  }

  const radius =
    typeof fluidManualBrushRadius === "number"
      ? clampFluidManualBrushRadius(fluidManualBrushRadius)
      : 6;

  const onSliderChange = (_e, value) => {
    dispatch({ type: "SET_FLUID_MANUAL_BRUSH_RADIUS", radius: value });
  };

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.5,
        bgcolor: "#fff",
        color: "text.primary",
        borderBottom: "1px solid #e0e0e0",
        borderTop: "3px solid #1976d2",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          fontWeight: 700,
          color: "#1976d2",
          letterSpacing: "0.02em",
        }}
      >
        Brush Settings
      </Typography>

      <Typography
        variant="caption"
        sx={{ color: "text.secondary", mb: 0.5, display: "block" }}
      >
        Size (1–50 px)
      </Typography>
      <Slider
        value={radius}
        min={FLUID_MANUAL_BRUSH_RADIUS_MIN}
        max={FLUID_MANUAL_BRUSH_RADIUS_MAX}
        step={1}
        size="small"
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => `${v}px`}
        onChange={onSliderChange}
        sx={{
          mx: 0.5,
          color: "#1976d2",
          "& .MuiSlider-track": { backgroundColor: "#1976d2" },
          "& .MuiSlider-rail": { backgroundColor: "#e0e0e0", opacity: 1 },
          "& .MuiSlider-thumb": {
            backgroundColor: "#1976d2",
            width: 14,
            height: 14,
          },
        }}
      />
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1.5, mb: 0.75, color: "text.secondary" }}
      >
        Stroke or fill
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={mode}
        onChange={(_e, v) =>
          v && dispatch({ type: "SET_FLUID_BRUSH_DRAWING_MODE", mode: v })
        }
        sx={{
          bgcolor: "#f5f5f5",
          "& .MuiToggleButton-root": {
            color: "text.primary",
            borderColor: "#e0e0e0",
            textTransform: "none",
            py: 0.75,
            bgcolor: "#fff",
          },
          "& .MuiToggleButton-root.Mui-selected": {
            bgcolor: "#1976d2",
            color: "#fff",
            "&:hover": { bgcolor: "#1565c0" },
          },
        }}
      >
        <ToggleButton value="stroke">Stroke</ToggleButton>
        <ToggleButton value="fill">Fill</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}
